import clsx from "clsx";
import { useState, useEffect } from "react";

import { CronPreview } from "./CronPreview";

type ScheduleMode = "daily" | "interval";

const DAYS = [
  { label: "Mo", value: 1 },
  { label: "Tu", value: 2 },
  { label: "We", value: 3 },
  { label: "Th", value: 4 },
  { label: "Fr", value: 5 },
  { label: "Sa", value: 6 },
  { label: "Su", value: 0 },
];

interface ScheduleInputProps {
  cron: string;
  timezone: string;
  onChange: (cron: string) => void;
}

function parseCronToState(cron: string): {
  mode: ScheduleMode;
  time: string;
  days: number[];
  intervalValue: number;
  intervalUnit: "minutes" | "hours";
} {
  const parts = cron.split(" ");
  if (parts.length !== 5) {
    return {
      mode: "daily",
      time: "09:00",
      days: [1, 2, 3, 4, 5],
      intervalValue: 30,
      intervalUnit: "minutes",
    };
  }

  const [minute, hour, , , dow] = parts;

  // Check if it's an interval pattern
  if (minute.startsWith("*/") || hour.startsWith("*/")) {
    if (hour.startsWith("*/")) {
      return {
        mode: "interval",
        time: "09:00",
        days: [1, 2, 3, 4, 5],
        intervalValue: parseInt(hour.replace("*/", ""), 10) || 1,
        intervalUnit: "hours",
      };
    }
    return {
      mode: "interval",
      time: "09:00",
      days: [1, 2, 3, 4, 5],
      intervalValue: parseInt(minute.replace("*/", ""), 10) || 30,
      intervalUnit: "minutes",
    };
  }

  // Daily mode
  const h = parseInt(hour, 10) || 9;
  const m = parseInt(minute, 10) || 0;
  const timeStr = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

  let days: number[] = DAYS.map((day) => day.value);
  if (dow !== "*") {
    days = dow.split(",").flatMap((part) => {
      if (part.includes("-")) {
        const [start, end] = part.split("-").map(Number);
        const result: number[] = [];
        for (let i = start; i <= end; i++) result.push(i);
        return result;
      }
      return [parseInt(part, 10)];
    });
  }

  return { mode: "daily", time: timeStr, days, intervalValue: 30, intervalUnit: "minutes" };
}

function buildCron(
  mode: ScheduleMode,
  time: string,
  days: number[],
  intervalValue: number,
  intervalUnit: "minutes" | "hours",
): string {
  if (mode === "interval") {
    if (intervalUnit === "hours") {
      return `0 */${intervalValue} * * *`;
    }
    return `*/${intervalValue} * * * *`;
  }

  const [h, m] = time.split(":").map(Number);
  const dayStr =
    days.length === 7 || days.length === 0 ? "*" : days.sort((a, b) => a - b).join(",");
  return `${m} ${h} * * ${dayStr}`;
}

export function ScheduleInput({ cron, timezone, onChange }: ScheduleInputProps): JSX.Element {
  const initial = parseCronToState(cron);
  const [mode, setMode] = useState<ScheduleMode>(initial.mode);
  const [time, setTime] = useState(initial.time);
  const [days, setDays] = useState<number[]>(initial.days);
  const [intervalValue, setIntervalValue] = useState(initial.intervalValue);
  const [intervalUnit, setIntervalUnit] = useState<"minutes" | "hours">(initial.intervalUnit);

  useEffect(() => {
    const newCron = buildCron(mode, time, days, intervalValue, intervalUnit);
    if (newCron !== cron) {
      onChange(newCron);
    }
  }, [mode, time, days, intervalValue, intervalUnit]);

  function toggleDay(day: number) {
    setDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
  }

  return (
    <div className="space-y-3">
      {/* Mode toggle */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => setMode("daily")}
          className={clsx(
            "rounded px-2.5 py-1 text-xs transition-all",
            mode === "daily"
              ? "border border-cyber-cyan/40 bg-cyber-cyan/20 text-cyber-cyan"
              : "border border-cyber-border text-cyber-muted hover:text-cyber-text",
          )}
        >
          Daily
        </button>
        <button
          type="button"
          onClick={() => setMode("interval")}
          className={clsx(
            "rounded px-2.5 py-1 text-xs transition-all",
            mode === "interval"
              ? "border border-cyber-cyan/40 bg-cyber-cyan/20 text-cyber-cyan"
              : "border border-cyber-border text-cyber-muted hover:text-cyber-text",
          )}
        >
          Interval
        </button>
      </div>

      {mode === "daily" ? (
        <div className="space-y-2">
          {/* Time picker */}
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="rounded border border-cyber-border bg-cyber-bg px-3 py-1.5 text-sm text-cyber-text focus:border-cyber-cyan/50 focus:outline-none"
          />

          {/* Day-of-week toggles */}
          <div className="flex items-center gap-1">
            {DAYS.map((d) => (
              <button
                key={d.value}
                type="button"
                onClick={() => toggleDay(d.value)}
                className={clsx(
                  "h-8 w-8 rounded text-[11px] transition-all",
                  days.includes(d.value)
                    ? "border border-cyber-cyan/40 bg-cyber-cyan/20 text-cyber-cyan"
                    : "border border-cyber-border text-cyber-muted hover:text-cyber-text",
                )}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-xs text-cyber-muted">Every</span>
          <input
            type="number"
            min={5}
            max={intervalUnit === "hours" ? 24 : 60}
            value={intervalValue}
            onChange={(e) => setIntervalValue(Math.max(5, parseInt(e.target.value, 10) || 5))}
            className="w-16 rounded border border-cyber-border bg-cyber-bg px-2 py-1.5 text-sm text-cyber-text focus:border-cyber-cyan/50 focus:outline-none"
          />
          <select
            value={intervalUnit}
            onChange={(e) => setIntervalUnit(e.target.value as "minutes" | "hours")}
            className="rounded border border-cyber-border bg-cyber-bg px-2 py-1.5 text-sm text-cyber-text focus:border-cyber-cyan/50 focus:outline-none"
          >
            <option value="minutes">minutes</option>
            <option value="hours">hours</option>
          </select>
        </div>
      )}

      <CronPreview
        cron={buildCron(mode, time, days, intervalValue, intervalUnit)}
        timezone={timezone}
      />
    </div>
  );
}
